import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { strapiService } from '../../../utils/api';
import strapiAPI from '../../../utils/api';
import AlertService from '../../../utils/alertService';
import billingService from '../../../utils/billingService';

const CompanyForm = ({ selectedCompany, onSave, onCancel, isLoading, onFielUploadComplete, mode = 'create', userType = 'company_admin' }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Cliente de despacho (iguala) — solo super_admin. En edit se carga el estado
  // real de la suscripción; en create viaja con el payload y el padre lo aplica
  // después de crear la empresa.
  const [despacho, setDespacho] = useState({ enabled: false, known: mode === 'create', updating: false });

  // FIXED: Improved form data initialization with better field mapping
  const [formData, setFormData] = useState({
    name: selectedCompany?.name || selectedCompany?.businessName || '',
    rfc: selectedCompany?.rfc || '',
    address: selectedCompany?.address || selectedCompany?.fiscal_address || '',
    contact_email: selectedCompany?.contact_email || selectedCompany?.contactEmail || selectedCompany?.adminEmail || '',
    contact_phone: selectedCompany?.contact_phone || selectedCompany?.contactPhone || selectedCompany?.phone || '',
    logo: selectedCompany?.logo?.id || selectedCompany?.logo || null,
    satPassword: selectedCompany?.satPassword || '',
    syncSchedule: selectedCompany?.syncSchedule || 'daily',
    state: selectedCompany?.state || 'activa',
    is_despacho: false
  });

  const [errors, setErrors] = useState({});
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(() => {
    if (selectedCompany?.logo?.url) return selectedCompany?.logo?.url;
    if (selectedCompany?.logo && typeof selectedCompany?.logo === 'string') return selectedCompany?.logo;
    return null;
  });
  
  // FIEL Upload State
  const [uploadState, setUploadState] = useState({
    keyFile: null,
    certFile: null,
    fielPassword: '',
    isUploading: false,
    uploadProgress: 0,
    validationStatus: null,
    fielErrors: {}
  });

  const keyFileRef = useRef(null);
  const certFileRef = useRef(null);
  const logoFileRef = useRef(null);

  const stateOptions = [
    { value: 'activa', label: 'Activa' },
    { value: 'inactiva', label: 'Inactiva' },
    { value: 'suspendida', label: 'Suspendida' }
  ];

  const syncScheduleOptions = [
    { value: 'daily', label: 'Diario' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensual' }
  ];

  // Check if user can access SAT integration tab
  const canAccessSATTab = userType === 'company_admin';

  const validateRFC = (rfc) => {
    if (!rfc) return false;
    const cleanRFC = rfc?.replace(/\s/g, '');
    return cleanRFC?.length >= 12 && cleanRFC?.length <= 13;
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern?.test(email);
  };

  const validatePhone = (phone) => {
    const phonePattern = /^[0-9]{10}$/;
    return phonePattern?.test(phone?.replace(/\s/g, ''));
  };

  // Handle company state change with SweetAlert confirmation
  const handleStateChange = async (newState) => {
    if (!selectedCompany?.id) {
      console.error('No company ID available for state update');
      return;
    }

    const currentState = formData?.state || selectedCompany?.state || 'activa';

    // If state hasn't changed, just update local state
    if (newState === currentState) {
      setFormData(prev => ({ ...prev, state: newState }));
      return;
    }

    const stateLabels = {
      'activa': 'Activa',
      'inactiva': 'Inactiva',
      'suspendida': 'Suspendida'
    };

    const confirmationMessages = {
      'activa': '¿Está seguro de que desea activar esta empresa? Esto restaurará su acceso al sistema.',
      'inactiva': '¿Está seguro de que desea marcar esta empresa como inactiva? Esto limitará su acceso.',
      'suspendida': '¿Está seguro de que desea suspender esta empresa? Esto afectará su acceso al sistema.'
    };

    try {
      // Show confirmation dialog with SweetAlert
      const confirmed = await AlertService?.confirm(
        `Cambiar Estado a "${stateLabels[newState]}"`,
        confirmationMessages[newState],
        {
          confirmText: `Sí, cambiar a ${stateLabels[newState]}`,
          cancelText: 'Cancelar'
        }
      );

      if (!confirmed) {
        return;
      }

      setIsUpdatingStatus(true);

      console.log('🔄 Updating company state:', { companyId: selectedCompany?.id, state: newState });

      // Call the update endpoint with the new state
      const response = await strapiService?.companies?.updateCompany(selectedCompany?.id, { state: newState });

      console.log('✅ Company state updated successfully:', response);

      // Update local form data
      setFormData(prev => ({ ...prev, state: newState }));

      // Get company name from multiple possible sources
      const companyName = formData?.name || selectedCompany?.name || selectedCompany?.businessName || 'la empresa';

      // Show success message
      await AlertService?.success(
        'Estado Actualizado',
        `La empresa "${companyName}" ahora está "${stateLabels[newState]}".`
      );

    } catch (error) {
      console.error('❌ Company state update failed:', error);

      // Extract error message for user-friendly display
      let errorMessage = 'Error desconocido al actualizar el estado de la empresa.';

      if (error?.response?.status === 404) {
        errorMessage = 'La empresa no fue encontrada.';
      } else if (error?.response?.status === 403) {
        errorMessage = 'No tiene permisos para realizar esta acción.';
      } else if (error?.response?.status === 400) {
        errorMessage = error?.response?.data?.error?.message || 'Datos inválidos enviados al servidor.';
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error?.response?.data?.error?.message;
      } else if (error?.message) {
        errorMessage = error?.message;
      }

      // Show error message with SweetAlert
      await AlertService?.error(
        'Error al Actualizar Estado',
        errorMessage
      );

      // Revert to previous state on error
      setFormData(prev => ({ ...prev, state: currentState }));

    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Cargar el estado real del check de despacho (solo edit + super_admin)
  useEffect(() => {
    let cancelled = false;
    const loadDespacho = async () => {
      if (mode !== 'edit' || userType !== 'super_admin' || !selectedCompany?.id) return;
      try {
        const data = await billingService?.admin?.getDespacho(selectedCompany.id);
        if (!cancelled) {
          setDespacho({ enabled: !!data?.enabled, known: true, updating: false });
        }
      } catch (error) {
        console.error('❌ Error loading despacho state:', error);
        // known queda en false → el toggle se muestra deshabilitado, nunca con
        // un valor inventado que pudiera apagar el plan por accidente.
      }
    };
    loadDespacho();
    return () => { cancelled = true; };
  }, [mode, userType, selectedCompany?.id]);

  // Toggle inmediato (con confirmación) del cliente de despacho — mismo patrón
  // que el cambio de Estado. Solo edit mode; en create viaja en el payload.
  const handleDespachoToggle = async () => {
    if (!selectedCompany?.id || !despacho.known || despacho.updating) return;

    const turningOn = !despacho.enabled;
    const companyName = formData?.name || selectedCompany?.name || 'la empresa';

    const confirmed = await AlertService?.confirm(
      turningOn ? 'Marcar como cliente de despacho' : 'Quitar plan de despacho',
      turningOn
        ? `"${companyName}" tendrá acceso completo sin cobro (empleados ilimitados, incluido en la iguala del despacho).`
        : `"${companyName}" perderá el plan de despacho: al siguiente uso se le pedirá contratar un plan para seguir usando la plataforma.`,
      {
        confirmText: turningOn ? 'Sí, marcar' : 'Sí, quitar',
        cancelText: 'Cancelar'
      }
    );
    if (!confirmed) return;

    setDespacho(prev => ({ ...prev, updating: true }));
    try {
      await billingService?.admin?.setDespacho(selectedCompany.id, turningOn);
      setDespacho({ enabled: turningOn, known: true, updating: false });
      await AlertService?.success(
        turningOn ? 'Cliente de despacho' : 'Plan de despacho retirado',
        turningOn
          ? `"${companyName}" quedó con acceso completo sin cobro.`
          : `"${companyName}" deberá contratar un plan para seguir usando la plataforma.`
      );
    } catch (error) {
      console.error('❌ Error updating despacho state:', error);
      setDespacho(prev => ({ ...prev, updating: false }));
      const errorMessage = error?.response?.data?.error?.message
        || error?.message
        || 'Error desconocido al actualizar el plan de despacho.';
      await AlertService?.error('Error al actualizar', errorMessage);
    }
  };

  const handleLogoSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Solo se permiten archivos de imagen (JPG, PNG, GIF)');
      return;
    }

    // Validate file size (max 2MB)
    if (file?.size > 2 * 1024 * 1024) {
      alert('El archivo no debe exceder 2MB');
      return;
    }

    setIsUploadingLogo(true);
    
    try {
      console.log('🔄 Starting logo upload process...');
      
      // Upload logo and get file ID
      const uploadedFile = await strapiService?.upload?.uploadLogo(file);
      
      setFormData(prev => ({
        ...prev,
        logo: uploadedFile?.id
      }));
      
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      
      console.log('✅ Logo uploaded successfully:', uploadedFile);
      alert('✅ Logo subido correctamente');
      
    } catch (error) {
      console.error('❌ Logo upload error:', error);
      
      // Show user-friendly error message
      let errorMessage = error?.message || 'Error desconocido al subir el logo';
      alert(`❌ Error al subir el logo:\n${errorMessage}\n\nIntente nuevamente o contacte soporte.`);
      
      // Reset file input
      if (logoFileRef?.current) {
        logoFileRef.current.value = '';
      }
      
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null
    }));
    setLogoFile(null);
    setLogoPreview(null);
    if (logoFileRef?.current) {
      logoFileRef.current.value = '';
    }
  };

  const handleFileSelect = (type, file) => {
    if (!file) return;

    // Validate file extension
    const validExtensions = type === 'key' ? ['.key'] : ['.cer'];
    const fileExtension = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
    
    if (!validExtensions?.includes(fileExtension)) {
      setUploadState(prev => ({
        ...prev,
        fielErrors: {
          ...prev?.fielErrors,
          [type]: `Archivo debe tener extensión ${validExtensions?.join(' o ')}`
        }
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      setUploadState(prev => ({
        ...prev,
        fielErrors: {
          ...prev?.fielErrors,
          [type]: 'El archivo no debe exceder 5MB'
        }
      }));
      return;
    }

    setUploadState(prev => ({
      ...prev,
      [type === 'key' ? 'keyFile' : 'certFile']: file,
      fielErrors: {
        ...prev?.fielErrors,
        [type]: ''
      }
    }));
  };

  const handleRemoveFile = (type) => {
    setUploadState(prev => ({
      ...prev,
      [type === 'key' ? 'keyFile' : 'certFile']: null,
      validationStatus: null,
      fielErrors: {
        ...prev?.fielErrors,
        [type]: ''
      }
    }));

    if (type === 'key' && keyFileRef?.current) {
      keyFileRef.current.value = '';
    }
    if (type === 'cert' && certFileRef?.current) {
      certFileRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateGeneralForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'El nombre de la empresa es requerido';
    }

    if (!formData?.rfc?.trim()) {
      newErrors.rfc = 'El RFC es requerido';
    } else if (!validateRFC(formData?.rfc?.toUpperCase())) {
      newErrors.rfc = 'RFC debe tener entre 12 y 13 caracteres';
    }

    if (!formData?.contact_email?.trim()) {
      newErrors.contact_email = 'El email del contacto es requerido';
    } else if (!validateEmail(formData?.contact_email)) {
      newErrors.contact_email = 'Formato de email inválido';
    }

    if (formData?.contact_phone?.trim() && !validatePhone(formData?.contact_phone)) {
      newErrors.contact_phone = 'Teléfono debe tener 10 dígitos';
    }

    return newErrors;
  };

  const validateSATForm = () => {
    // FIEL validation is optional - no required fields in SAT tab
    const newErrors = {};
    return newErrors;
  };

  const validateForm = () => {
    // Only validate the current active tab
    let newErrors = {};

    if (activeTab === 'general') {
      newErrors = validateGeneralForm();
    } else if (activeTab === 'sat' && canAccessSATTab) {
      newErrors = validateSATForm();
    }

    console.log('🔍 Validation errors found:', newErrors);
    console.log('📝 Current form data:', formData);
    console.log('📑 Active tab:', activeTab);

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle SAT Integration update (separate endpoint)
  const handleSATIntegrationUpdate = async () => {
    console.log('🔵 handleSATIntegrationUpdate called');
    console.log('🔍 uploadState:', uploadState);
    console.log('🔍 selectedCompany?.id:', selectedCompany?.id);

    if (!selectedCompany?.id) {
      await AlertService?.error('Error', 'No se pudo identificar la empresa para actualizar integración SAT');
      return;
    }

    // Check if at least one field has data
    const hasData = uploadState?.keyFile || uploadState?.certFile || uploadState?.fielPassword?.trim();

    console.log('📊 hasData check:', {
      hasKeyFile: !!uploadState?.keyFile,
      hasCertFile: !!uploadState?.certFile,
      hasPassword: !!uploadState?.fielPassword?.trim(),
      totalHasData: hasData
    });

    if (!hasData) {
      console.log('⚠️ No hay datos para actualizar');
      await AlertService?.warning(
        'Sin cambios',
        'Debe proporcionar al menos un archivo FIEL (.key o .cer) o la contraseña FIEL para actualizar la integración SAT.'
      );
      return;
    }

    try {
      setUploadState(prev => ({ ...prev, isUploading: true }));
      AlertService?.loading('Actualizando integración SAT...', 'Por favor espere mientras se procesan los certificados FIEL.');

      let certFileId = null;
      let keyFileId = null;

      // STEP 1: Upload certificate file to Strapi if provided
      if (uploadState?.certFile) {
        console.log('📄 Uploading certificate file:', uploadState?.certFile?.name);
        console.log('📄 File object:', uploadState?.certFile);

        const certFormData = new FormData();
        certFormData.append('files', uploadState?.certFile);

        try {
          const certUploadResponse = await strapiAPI.post('/api/upload', certFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (certUploadResponse?.data?.[0]?.id) {
            certFileId = certUploadResponse?.data?.[0]?.id;
            console.log('✅ Certificate uploaded with ID:', certFileId);
          }
        } catch (uploadError) {
          console.error('❌ Error uploading certificate:', uploadError);
          throw new Error('Error al subir el archivo de certificado (.cer)');
        }
      }

      // STEP 2: Upload private key file to Strapi if provided
      if (uploadState?.keyFile) {
        console.log('🔑 Uploading private key file:', uploadState?.keyFile?.name);
        console.log('🔑 File object:', uploadState?.keyFile);

        const keyFormData = new FormData();
        keyFormData.append('files', uploadState?.keyFile);

        try {
          const keyUploadResponse = await strapiAPI.post('/api/upload', keyFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (keyUploadResponse?.data?.[0]?.id) {
            keyFileId = keyUploadResponse?.data?.[0]?.id;
            console.log('✅ Private key uploaded with ID:', keyFileId);
          }
        } catch (uploadError) {
          console.error('❌ Error uploading private key:', uploadError);
          throw new Error('Error al subir el archivo de llave privada (.key)');
        }
      }

      // STEP 3: Send file IDs and password to SAT integration endpoint
      const satData = {
        companyId: selectedCompany?.id
      };

      if (certFileId) {
        satData.fiel_certificate = certFileId;
      }

      if (keyFileId) {
        satData.fiel_private_key = keyFileId;
      }

      if (uploadState?.fielPassword?.trim()) {
        satData.fiel_password = uploadState?.fielPassword?.trim();
      }

      console.log('🚀 Sending SAT integration data:', satData);

      const response = await strapiService?.post(
        `companies/${selectedCompany?.id}/sat-integration`,
        satData
      );

      console.log('✅ SAT integration updated successfully:', response);

      AlertService?.close();
      await AlertService?.success(
        'Integración SAT actualizada',
        'Los certificados FIEL y configuración SAT se han actualizado correctamente.'
      );

      // Reset upload state
      setUploadState({
        keyFile: null,
        certFile: null,
        fielPassword: '',
        isUploading: false,
        uploadProgress: 0,
        validationStatus: null,
        fielErrors: {}
      });

      // Clear file inputs
      if (keyFileRef?.current) keyFileRef.current.value = '';
      if (certFileRef?.current) certFileRef.current.value = '';

    } catch (error) {
      console.error('❌ Error updating SAT integration:', error);

      AlertService?.close();

      // Handle specific errors
      if (error?.response?.status === 400) {
        // El backend ya envía un mensaje específico (CSD en vez de FIEL, certificado
        // vencido, contraseña incorrecta, RFC que no coincide…). Mostrarlo tal cual
        // en vez de un genérico, para que el usuario sepa QUÉ corregir.
        const backendMsg = error?.response?.data?.error?.message;
        const code = error?.response?.data?.error?.details?.code;
        const title = code === 'IS_CSD' ? 'Subiste un CSD, no la FIEL'
          : code === 'EXPIRED' ? 'Certificado vencido'
          : code === 'BAD_PASSWORD_OR_KEY' ? 'Contraseña o llave incorrecta'
          : code === 'RFC_MISMATCH' ? 'La FIEL no es de esta empresa'
          : 'Credencial FIEL inválida';
        await AlertService?.error(
          title,
          backendMsg || 'Los archivos deben tener extensión .cer (certificado) y .key (llave privada)'
        );
      } else if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          'No tiene permisos para actualizar la integración SAT de esta empresa'
        );
      } else if (error?.response?.status === 404) {
        await AlertService?.error(
          'Empresa no encontrada',
          'La empresa que intenta actualizar no existe'
        );
      } else {
        await AlertService?.error(
          'Error al actualizar',
          error?.response?.data?.error?.message || 'Error al actualizar la integración SAT. Intente nuevamente.'
        );
      }
    } finally {
      setUploadState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    console.log('🎯 Form submitted - validating...');

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    console.log('✅ Form validation passed - preparing data...');

    try {
      // DIFFERENT HANDLING: SAT tab uses different endpoint
      if (activeTab === 'sat') {
        console.log('🔐 Processing SAT Integration update...');
        await handleSATIntegrationUpdate();
        return; // Exit early - SAT update has its own flow
      }

      // GENERAL TAB: Normal company data update
      const apiData = {
        name: formData?.name?.trim(),
        rfc: formData?.rfc?.toUpperCase()?.trim(),
        contact_email: formData?.contact_email?.trim(),
        ...(formData?.address?.trim() && { address: formData?.address?.trim() }),
        ...(formData?.contact_phone?.trim() && { contact_phone: formData?.contact_phone?.trim() }),
        ...(formData?.logo && { logo: formData?.logo }),
        // Solo en alta: el padre aplica el plan de despacho tras crear la empresa.
        // En edición el toggle es inmediato (handleDespachoToggle), no viaja aquí.
        ...(mode === 'create' && userType === 'super_admin' ? { is_despacho: !!formData?.is_despacho } : {})
      };

      // ENHANCED: Add company ID for updates
      if (mode === 'edit' && selectedCompany?.id) {
        apiData.id = selectedCompany?.id;
      }

      console.log('📤 Sending general data to parent:', apiData);

      onSave(apiData);

    } catch (error) {
      console.error('Error preparing company data:', error);
      alert('Error al procesar los datos. Intente nuevamente.');
    }
  };

  // REMOVED: handleTabSave function since it's not functional and confusing

  // FIXED: Add useEffect to ensure form data is properly populated when selectedCompany changes
  useEffect(() => {
    if (selectedCompany && mode === 'edit') {
      console.log('🔄 Populating form with company data:', selectedCompany);
      
      setFormData({
        name: selectedCompany?.name || selectedCompany?.businessName || '',
        rfc: selectedCompany?.rfc || '',
        address: selectedCompany?.address || selectedCompany?.fiscal_address || '',
        contact_email: selectedCompany?.contact_email || selectedCompany?.contactEmail || selectedCompany?.adminEmail || '',
        contact_phone: selectedCompany?.contact_phone || selectedCompany?.contactPhone || selectedCompany?.phone || '',
        logo: selectedCompany?.logo?.id || selectedCompany?.logo || null,
        satPassword: selectedCompany?.satPassword || '',
        syncSchedule: selectedCompany?.syncSchedule || 'daily',
        state: selectedCompany?.state || 'activa'
      });

      // FIXED: Logo URL construction following exact backend documentation patterns
      const constructLogoUrl = () => {
        const API_BASE_URL = import.meta.env?.VITE_STRAPI_API_URL || 'https://dev-api.minomina.coal.mx';
        
        console.log('🔍 Logo URL Debug Info:');
        console.log('- API_BASE_URL:', API_BASE_URL);
        console.log('- selectedCompany full structure:', JSON.stringify(selectedCompany, null, 2));
        
        // CORRECTED: Follow exact backend documentation patterns for logo access
        // Pattern 1: For Company Admin/Employee (single company) - profileData?.company?.logo?.url
        // Pattern 2: For Super Admin (companies array) - company?.logo?.url from companies array
        
        let logoUrl = null;
        let thumbnailUrl = null;
        
        // Check if this is profile data with company property (Company Admin/Employee pattern)
        if (selectedCompany?.company?.logo) {
          logoUrl = selectedCompany?.company?.logo?.url;
          thumbnailUrl = selectedCompany?.company?.logo?.formats?.thumbnail?.url;
          console.log('📍 Using Company Admin/Employee pattern (profileData.company.logo)');
        } 
        // Check if this is direct company data (Super Admin companies array pattern)
        else if (selectedCompany?.logo) {
          logoUrl = selectedCompany?.logo?.url;
          thumbnailUrl = selectedCompany?.logo?.formats?.thumbnail?.url;
          console.log('📍 Using Super Admin pattern (company.logo)');
        }
        // Check for companies array structure (alternative Super Admin pattern)
        else if (selectedCompany?.companies && Array.isArray(selectedCompany?.companies)) {
          const company = selectedCompany?.companies?.find(c => c?.id === selectedCompany?.id);
          if (company?.logo) {
            logoUrl = company?.logo?.url;
            thumbnailUrl = company?.logo?.formats?.thumbnail?.url;
            console.log('📍 Using companies array pattern');
          }
        }
        
        console.log('- Raw logoUrl found:', logoUrl);
        console.log('- Raw thumbnailUrl found:', thumbnailUrl);
        
        // Construct full URL according to backend documentation
        if (thumbnailUrl) {
          // Prefer thumbnail for preview (smaller file, better performance)
          const fullUrl = `${API_BASE_URL}${thumbnailUrl}`;
          console.log('✅ Using thumbnail URL:', fullUrl);
          return fullUrl;
        } else if (logoUrl) {
          // Use original image if no thumbnail available
          const fullUrl = `${API_BASE_URL}${logoUrl}`;
          console.log('✅ Using original image URL:', fullUrl);
          return fullUrl;
        }
        
        console.log('❌ No logo URL found in any expected pattern');
        return null;
      };

      const fullLogoUrl = constructLogoUrl();
      if (fullLogoUrl) {
        setLogoPreview(fullLogoUrl);
        console.log('✅ Logo preview URL set:', fullLogoUrl);
      } else {
        setLogoPreview(null);
        console.log('ℹ️ No logo URL available - will show upload placeholder');
      }

      console.log('✅ Form data populated successfully');
    }
  }, [selectedCompany, mode]);

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex space-x-0">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'general' ?'text-primary border-primary bg-primary/5' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-center space-x-1 sm:space-x-2">
              <Icon name="Building2" size={16} />
              <span>Datos Generales</span>
            </div>
          </button>

          <button
            onClick={() => canAccessSATTab ? setActiveTab('sat') : null}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
              !canAccessSATTab
                ? 'text-muted-foreground/50 border-transparent cursor-not-allowed'
                : activeTab === 'sat' ?'text-primary border-primary bg-primary/5' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
            }`}
            disabled={!canAccessSATTab}
          >
            <div className="flex items-center justify-center space-x-1 sm:space-x-2">
              <Icon name="Shield" size={16} />
              <span>Integración SAT</span>
              {!canAccessSATTab && (
                <Icon name="Lock" size={14} className="ml-1" />
              )}
            </div>
          </button>
        </div>

        {!canAccessSATTab && (
          <div className="px-3 sm:px-6 py-2 bg-muted/30 border-t border-border">
            <p className="text-xs text-muted-foreground">
              <Icon name="Info" size={12} className="inline mr-1" />
              <span className="hidden sm:inline">Solo los administradores de empresa pueden gestionar la configuración de integración SAT</span>
              <span className="sm:hidden">Solo admins pueden gestionar SAT</span>
            </p>
          </div>
        )}
      </div>
      {/* Tab Content */}
      <div className="p-3 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* General Data Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Datos Generales de la Empresa
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Complete la información básica de la empresa según los requerimientos del SAT
                  </p>
                </div>
                {selectedCompany && (
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    formData?.state === 'activa'
                      ? 'bg-success/10 text-success'
                      : formData?.state === 'inactiva'
                        ? 'bg-muted-foreground/10 text-muted-foreground'
                        : 'bg-warning/10 text-warning'
                  }`}>
                    {formData?.state === 'activa'
                      ? 'Activa'
                      : formData?.state === 'inactiva'
                        ? 'Inactiva'
                        : 'Suspendida'}
                  </div>
                )}
              </div>
              
              {/* Business Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                  Información General de la Empresa
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Nombre o Razón Social"
                    type="text"
                    placeholder="Ingrese la razón social de la empresa"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    required
                  />

                  <Input
                    label="RFC (12-13 caracteres)"
                    type="text"
                    placeholder="ABCD123456XYZ"
                    value={formData?.rfc}
                    onChange={(e) => handleInputChange('rfc', e?.target?.value?.toUpperCase())}
                    error={errors?.rfc}
                    maxLength={13}
                    required
                  />
                </div>

                <Input
                  label="Dirección Fiscal (Opcional)"
                  type="text"
                  placeholder="Calle, número, colonia"
                  value={formData?.address}
                  onChange={(e) => handleInputChange('address', e?.target?.value)}
                  error={errors?.address}
                />
              </div>

              {/* Contact Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                  Información de Contacto
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Email del Administrador"
                    type="email"
                    placeholder="admin@empresa.com"
                    value={formData?.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e?.target?.value)}
                    error={errors?.contact_email}
                    required
                  />

                  <Input
                    label="Teléfono de Contacto (Opcional)"
                    type="tel"
                    placeholder="5512345678"
                    value={formData?.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e?.target?.value)}
                    error={errors?.contact_phone}
                    maxLength={10}
                  />
                </div>

                {mode === 'create' && (
                  <div className="bg-muted/30 rounded-lg p-4 border border-border">
                    <div className="flex items-start space-x-3">
                      <Icon name="Info" size={16} className="text-primary mt-0.5" />
                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">Creación Automática de Usuario Administrador</p>
                        <p>Se creará automáticamente un usuario Company Admin usando el email de contacto. Si el email ya existe pero el usuario está inactivo, será reactivado automáticamente.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Logo Upload Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                  Logo de la Empresa (Opcional)
                </h3>

                <div className="flex items-start space-x-6">
                  <div className="flex-1 max-w-md">
                    {!logoPreview ? (
                      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                        <input
                          ref={logoFileRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoSelect(e?.target?.files?.[0])}
                          className="hidden"
                        />
                        <Icon name="ImagePlus" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-sm text-muted-foreground mb-3">
                          {mode === 'edit' ? 'No hay logo cargado. Seleccione uno para actualizar.' : 'Seleccione el logo de su empresa'}
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Formatos: JPG, PNG, GIF | Máximo: 2MB
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => logoFileRef?.current?.click()}
                          iconName="Upload"
                          iconPosition="left"
                          size="sm"
                          loading={isUploadingLogo}
                          disabled={isUploadingLogo}
                        >
                          {isUploadingLogo ? 'Subiendo...' : 'Seleccionar Logo'}
                        </Button>
                      </div>
                    ) : (
                      <div className="border border-border rounded-lg p-4 bg-muted/20">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-white rounded border border-border flex items-center justify-center overflow-hidden">
                            <img 
                              src={logoPreview} 
                              alt={`Logo de ${selectedCompany?.name || 'empresa'}`}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                console.warn('❌ Logo image failed to load:', logoPreview);
                                console.log('🔍 Error details:', e);
                                // Show fallback icon on image load error
                                e.target.style.display = 'none';
                                const fallbackIcon = e?.target?.parentElement?.querySelector('.fallback-icon');
                                if (!fallbackIcon) {
                                  const icon = document.createElement('div');
                                  icon.className = 'fallback-icon flex items-center justify-center w-full h-full text-muted-foreground';
                                  icon.innerHTML = '<svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9,22 9,12 15,12 15,22"></polyline></svg>';
                                  e?.target?.parentElement?.appendChild(icon);
                                }
                              }}
                              onLoad={() => {
                                console.log('✅ Logo loaded successfully:', logoPreview);
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground mb-1">
                              Logo {mode === 'edit' ? 'actual de la empresa' : 'cargado correctamente'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {logoFile?.name || selectedCompany?.name || 'Logo de empresa'}
                            </p>
                            {mode === 'edit' && (
                              <p className="text-xs text-success mt-1">
                                ✅ Vista previa del logo actual cargado desde el servidor
                              </p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveLogo}
                            iconName="Trash2"
                            className="text-error hover:text-error"
                            title="Remover logo actual"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SAT Integration Tab */}
          {activeTab === 'sat' && canAccessSATTab && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Configuración de Integración SAT
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure la conexión y sincronización con el Sistema de Administración Tributaria
                </p>
              </div>
              
              {/* FIEL Certificates Section */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                  Certificados FIEL (Firma Electrónica Avanzada)
                </h3>
                
                {/* Security Notice */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Shield" size={20} className="text-primary mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Máxima Seguridad para sus Certificados</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Los archivos FIEL se encriptan con AES-256 y se almacenan en servidores seguros. 
                        Solo se utilizan para autenticación automática con el SAT durante las sincronizaciones programadas.
                      </p>
                    </div>
                  </div>
                </div>

                {/* FIEL upload sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                      Archivo Llave Privada (.key) *
                    </label>
                    
                    {!uploadState?.keyFile ? (
                      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                        <input
                          ref={keyFileRef}
                          type="file"
                          accept=".key"
                          onChange={(e) => handleFileSelect('key', e?.target?.files?.[0])}
                          className="hidden"
                        />
                        <Icon name="FileKey" size={32} className="text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">
                          Seleccione su archivo de llave privada
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => keyFileRef?.current?.click()}
                          iconName="Upload"
                          iconPosition="left"
                          size="sm"
                        >
                          Cargar Archivo .key
                        </Button>
                      </div>
                    ) : (
                      <div className="border border-border rounded-lg p-4 bg-muted/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Icon name="FileKey" size={20} className="text-primary" />
                            <div>
                              <p className="text-sm font-medium text-foreground">{uploadState?.keyFile?.name}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(uploadState?.keyFile?.size)}</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFile('key')}
                            iconName="Trash2"
                            className="text-error hover:text-error"
                          />
                        </div>
                      </div>
                    )}
                    
                    {uploadState?.fielErrors?.key && (
                      <p className="text-xs text-error">{uploadState?.fielErrors?.key}</p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">
                      Archivo Certificado (.cer) *
                    </label>
                    
                    {!uploadState?.certFile ? (
                      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                        <input
                          ref={certFileRef}
                          type="file"
                          accept=".cer"
                          onChange={(e) => handleFileSelect('cert', e?.target?.files?.[0])}
                          className="hidden"
                        />
                        <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">
                          Seleccione su archivo de certificado
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => certFileRef?.current?.click()}
                          iconName="Upload"
                          iconPosition="left"
                          size="sm"
                        >
                          Cargar Archivo .cer
                        </Button>
                      </div>
                    ) : (
                      <div className="border border-border rounded-lg p-4 bg-muted/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Icon name="FileText" size={20} className="text-primary" />
                            <div>
                              <p className="text-sm font-medium text-foreground">{uploadState?.certFile?.name}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(uploadState?.certFile?.size)}</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFile('cert')}
                            iconName="Trash2"
                            className="text-error hover:text-error"
                          />
                        </div>
                      </div>
                    )}
                    
                    {uploadState?.fielErrors?.cert && (
                      <p className="text-xs text-error">{uploadState?.fielErrors?.cert}</p>
                    )}
                  </div>
                </div>

                {/* FIEL Password Input */}
                <div className="max-w-md">
                  <Input
                    label="Contraseña de la FIEL"
                    type="password"
                    placeholder="Ingrese la contraseña de su certificado FIEL"
                    value={uploadState?.fielPassword}
                    onChange={(e) => setUploadState(prev => ({ 
                      ...prev, 
                      fielPassword: e?.target?.value,
                      fielErrors: { ...prev?.fielErrors, fielPassword: '' }
                    }))}
                    error={uploadState?.fielErrors?.fielPassword}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-border">
            <div className="space-y-4">
              {/* Cliente de despacho (iguala) — solo super_admin */}
              {userType === 'super_admin' && activeTab === 'general' && (
                mode === 'edit' && selectedCompany ? (
                  <div className="flex flex-col space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Cliente de despacho (iguala)
                    </label>
                    <button
                      type="button"
                      onClick={handleDespachoToggle}
                      disabled={!despacho.known || despacho.updating}
                      className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
                        despacho.enabled
                          ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                          : 'bg-muted/30 text-muted-foreground border-border hover:text-foreground'
                      } ${(!despacho.known || despacho.updating) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Icon name={despacho.enabled ? 'BadgeCheck' : 'Badge'} size={16} />
                      <span>
                        {!despacho.known
                          ? 'Cargando…'
                          : despacho.enabled
                            ? 'Acceso completo sin cobro (activo)'
                            : 'Marcar como cliente de despacho'}
                      </span>
                    </button>
                    <p className="text-xs text-muted-foreground max-w-xs">
                      Plan interno sin cobro y sin límite de empleados para clientes
                      incluidos en la iguala del despacho.
                    </p>
                  </div>
                ) : mode === 'create' ? (
                  <label className="flex items-start gap-2 cursor-pointer select-none max-w-xs">
                    <input
                      type="checkbox"
                      checked={!!formData?.is_despacho}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_despacho: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 rounded border-border accent-emerald-600"
                    />
                    <span>
                      <span className="text-sm font-medium text-foreground block">
                        Cliente de despacho (iguala)
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Nace con acceso completo sin cobro y sin límite de empleados.
                      </span>
                    </span>
                  </label>
                ) : null
              )}

              {mode === 'edit' && activeTab === 'general' && selectedCompany && (
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Estado de la Empresa
                  </label>
                  <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
                    {stateOptions?.map((option) => {
                      const isSelected = formData?.state === option?.value;
                      const stateColors = {
                        'activa': 'bg-success text-white hover:bg-success/90',
                        'inactiva': 'bg-muted-foreground text-white hover:bg-muted-foreground/90',
                        'suspendida': 'bg-warning text-white hover:bg-warning/90'
                      };

                      return (
                        <button
                          key={option?.value}
                          type="button"
                          onClick={() => handleStateChange(option?.value)}
                          disabled={isUpdatingStatus}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                            isSelected
                              ? stateColors[option?.value]
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                          } ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center space-x-2">
                            <Icon
                              name={
                                option?.value === 'activa' ? 'CheckCircle' :
                                option?.value === 'inactiva' ? 'MinusCircle' :
                                'Pause'
                              }
                              size={16}
                            />
                            <span>{option?.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
                iconName="X"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                loading={isLoading}
                iconName="CheckCircle"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                <span className="hidden sm:inline">{mode === 'edit' ? 'Actualizar Empresa' : 'Crear Empresa'}</span>
                <span className="sm:hidden">{mode === 'edit' ? 'Actualizar' : 'Crear'}</span>
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyForm;